import { TARGET_RANGES } from '~/src/DataType/TargetRange/constants'
import { HARVEST_BASED_HARVESTING_FLOW_ROUTE, HARVESTING_FLOW } from '~/src/Flow/constants'
import createLogger from '~/src/Lib/Logging'
import { lazyWithRetries } from '~/src/Lib/Utils'
import { TASK_EDITOR, TASK_EDITOR_ROUTE } from '~/src/Task/TaskManager/constants'

import Forbidden from '../Routes/Forbidden'
import NotFound from '../Routes/NotFound'

const AutomaitCapacityDefaultDialog = lazyWithRetries(() =>
  import('~/src/Facility/Dialogs/AutomaitIrrigationLimits').then(({ AutomaitCapacityDefaultDialog: C }) => ({
    default: C,
  })),
)
const AutomaitCapacityGroupDialog = lazyWithRetries(() =>
  import('~/src/Facility/Dialogs/AutomaitIrrigationLimits').then(({ AutomaitCapacityGroupDialog: C }) => ({
    default: C,
  })),
)
const AutomaitCapacityGroupDeleteDialog = lazyWithRetries(() =>
  import('~/src/Facility/Dialogs/AutomaitIrrigationLimits').then(({ AutomaitCapacityGroupDeleteDialog: C }) => ({
    default: C,
  })),
)
const BulkManualReadingForm = lazyWithRetries(() => import('~/src/Readings/ManualReading/BulkForm'))
const ConfigureDevice = lazyWithRetries(() => import('~/src/Setup/Devices/ConfigureDevice'))
const CultivarsForm = lazyWithRetries(() => import('~/src/Cultivars/Form'))
const DayNightTargetRangeView = lazyWithRetries(() => import('~/src/DataType/TargetRange/View/DayNightView'))
const DayNightTargetRangesDialog = lazyWithRetries(() => import('~/src/DataType/TargetRange/DayNightDialog'))
const DeviceActivation = lazyWithRetries(() => import('~/src/Device/ActivationDialog'))
const DeviceSearch = lazyWithRetries(() => import('~/src/Device/ActivationDialog/Search'))
const FacilityForm = lazyWithRetries(() => import('~/src/Facility/Form'))
const FlowDiscardRoute = lazyWithRetries(() => import('~/src/Flow/Discard/Route'))
const HarvestingFlow = lazyWithRetries(() => import('~/src/Flow/Harvesting'))
const HarvestView = lazyWithRetries(() => import('~/src/Annotations/HarvestView'))
const InventoryModal = lazyWithRetries(() => import('~/src/Inventory/Modal'))
const IrrigationControllerDelete = lazyWithRetries(() => import('~/src/Irrigation/ControllerDialog/Delete'))
const IrrigationControllerDialog = lazyWithRetries(() => import('~/src/Irrigation/ControllerDialog'))
const IrrigationJournalLogDialogsWrapper = lazyWithRetries(() => import('~/src/Irrigation/Journal/Wrapper'))
const IrrigationScheduleDelete = lazyWithRetries(() => import('~/src/Irrigation/Delete'))
const IrrigationSchedule = lazyWithRetries(() =>
  import('~/src/Irrigation/Dialog').then(({ IrrigationScheduleDialogRoute: isdRoute }) => ({
    default: isdRoute,
  })),
)
const LightScheduleDelete = lazyWithRetries(() => import('~/src/Room/LightSchedule/Delete'))
const LightScheduleDialogRoute = lazyWithRetries(() => import('~/src/Room/LightSchedule/Dialog'))
const LightScheduleView = lazyWithRetries(() => import('~/src/Room/LightSchedule/View'))
const ManualReadingDelete = lazyWithRetries(() => import('~/src/Readings/ManualReading/Delete'))
const ManualReadingForm = lazyWithRetries(() => import('~/src/Annotations/ReadingForm'))
const ManualReadingView = lazyWithRetries(() => import('~/src/Readings/ManualReading/View'))
const ModbusRegistersDialog = lazyWithRetries(() => import('~/src/Irrigation/ModbusRegistersDialog'))
const NoteDelete = lazyWithRetries(() => import('~/src/Note/Delete'))
const NoteForm = lazyWithRetries(() => import('~/src/Note/Form'))
const NoteView = lazyWithRetries(() => import('~/src/Note/View'))
const NotificationView = lazyWithRetries(() => import('~/src/Notification/View'))
const PesticideApplicationDelete = lazyWithRetries(() => import('~/src/IPM/Application/Delete'))
const PesticideApplicationForm = lazyWithRetries(() => import('~/src/IPM/Application/Form'))
const PesticideApplicationView = lazyWithRetries(() => import('~/src/IPM/Application/View'))
const PesticideDelete = lazyWithRetries(() => import('~/src/IPM/Pesticide/Delete'))
const PesticideForm = lazyWithRetries(() => import('~/src/IPM/Pesticide/FormDialog'))
const PhSensorCalibration = lazyWithRetries(() => import('~/src/Device/PhSensorCalibration'))
const RoomForm = lazyWithRetries(() => import('~/src/Room/Form'))
const SubstrateSettings = lazyWithRetries(() => import('~/src/SubstrateSettings/Dialog'))
const SupportForm = lazyWithRetries(() => import('~/src/Support/SupportForm'))
const TaskDelete = lazyWithRetries(() => import('~/src/Task/Delete'))
const TaskForm = lazyWithRetries(() => import('~/src/Task/TaskFormRoute'))
const TaskManager = lazyWithRetries(() => import('~/src/Task/TaskManager/Dialog/'))
const TaskReviewHours = lazyWithRetries(() => import('~/src/Task/ReviewHours'))
const TaskView = lazyWithRetries(() => import('~/src/Task/TaskViewRoute'))
const TargetRangeDelete = lazyWithRetries(() => import('~/src/DataType/TargetRange/Delete'))

const logger = createLogger('Dialog/routes')
/**
 * Defines dialog routes Default route pattern is /<1stLevelKey>/:id/<2ndLevelKey> When second level key is "new" or
 * "default", the pattern is /<1stLevelKey>/<2ndLevelKey> Individual second level properties can override the pattern
 * and define alias routes via the "pattern" and "aliases" properties
 */
export const SCHEMA_ACTION_TO_ROUTE = {
  automaitCapacity: {
    default: {
      Component: AutomaitCapacityDefaultDialog,
    },
  },
  automaitCapacityGroups: {
    new: {
      Component: AutomaitCapacityGroupDialog,
    },
    edit: {
      Component: AutomaitCapacityGroupDialog,
    },
    deleting: {
      Component: AutomaitCapacityGroupDeleteDialog,
    },
  },
  devices: {
    default: {
      Component: DeviceActivation,
      actions: ['change_device'],
    },
    calibrate: {
      Component: PhSensorCalibration,
      actions: ['change_device'],
      // TODO: feature flag?
    },
    search: {
      Component: DeviceSearch,
      actions: ['change_device'],
      pattern: '/devices/search',
    },
  },
  facility: {
    edit: {
      Component: FacilityForm,
      actions: ['change_facility'],
      pattern: '/facilityEdit',
    },
  },
  facilityCultivars: {
    new: {
      Component: CultivarsForm,
    },
    edit: {
      Component: CultivarsForm,
    },
  },
  flows: {
    discard: {
      Component: FlowDiscardRoute,
    },
  },
  harvests: {
    view: {
      Component: HarvestView,
      actions: ['view_harvest'],
    },
    [HARVESTING_FLOW]: {
      Component: HarvestingFlow,
      actions: ['harvest_flow'],
      pattern: HARVEST_BASED_HARVESTING_FLOW_ROUTE,
    },
  },
  harvestBatches: {
    view: {
      Component: InventoryModal,
    },
  },
  harvestPhases: {
    view: {
      Component: HarvestView,
    },
  },
  irrigationControllers: {
    new: {
      Component: IrrigationControllerDialog,
    },
    edit: {
      Component: IrrigationControllerDialog,
    },
    configure: {
      Component: IrrigationControllerDialog,
    },
    deleting: {
      Component: IrrigationControllerDelete,
    },
    view: {
      Component: IrrigationControllerDialog,
    },
    registers: {
      Component: ModbusRegistersDialog,
    },
  },
  irrigationSchedules: {
    new: {
      Component: IrrigationSchedule,
      actions: ['change_irrigation'],
      aliases: ['/irrigationTemplates/new'],
    },
    edit: {
      autoFetch: false,
      Component: IrrigationSchedule,
      actions: ['change_irrigation'],
      aliases: ['/irrigationTemplates/:id/edit'],
    },
    deleting: {
      autoFetch: false,
      Component: IrrigationScheduleDelete,
      actions: ['change_irrigation'],
      aliases: ['/irrigationTemplates/:id/deleting'],
    },
  },
  irrigation: {
    default: {
      Component: IrrigationJournalLogDialogsWrapper,
      pattern: '/irrigation/:type/:id',
    },
  },
  lightSchedules: {
    new: {
      Component: LightScheduleDialogRoute,
    },
    view: {
      Component: LightScheduleView,
    },
    edit: {
      Component: LightScheduleDialogRoute,
    },
    deleting: {
      Component: LightScheduleDelete,
    },
  },
  loggers: {
    edit: {
      Component: ConfigureDevice,
    },
  },
  notes: {
    new: {
      Component: NoteForm,
      aliases: ['/notes/new/:date'],
    },
    view: {
      Component: NoteView,
    },
    edit: {
      Component: NoteForm,
    },
    deleting: {
      Component: NoteDelete,
    },
  },
  notifications: {
    view: {
      Component: NotificationView,
    },
  },
  packages: {
    view: {
      Component: InventoryModal,
    },
  },
  pesticideApplications: {
    new: {
      Component: PesticideApplicationForm,
      actions: ['change_pesticide_application'],
      aliases: ['/pesticideApplications/new/:date'],
    },
    view: {
      Component: PesticideApplicationView,
    },
    edit: {
      Component: PesticideApplicationForm,
      actions: ['change_pesticide_application'],
    },
    deleting: {
      Component: PesticideApplicationDelete,
      actions: ['change_pesticide_application'],
    },
  },
  pesticides: {
    new: {
      Component: PesticideForm,
      actions: ['change_pesticide_application'],
    },
    edit: {
      Component: PesticideForm,
      actions: ['change_pesticide_application'],
    },
    deleting: {
      Component: PesticideDelete,
      actions: ['change_pesticide_application'],
    },
  },
  plants: {
    view: {
      Component: InventoryModal,
    },
  },
  plantBatches: {
    view: {
      Component: InventoryModal,
    },
  },
  readings: {
    new: {
      Component: BulkManualReadingForm,
      aliases: ['/readings/new/:date'],
    },
    view: {
      Component: ManualReadingView,
    },
    edit: {
      Component: ManualReadingForm,
    },
    deleting: {
      Component: ManualReadingDelete,
    },
  },
  rooms: {
    new: {
      Component: RoomForm,
      actions: ['change_facility'],
    },
    get edit() {
      return this.new
    },
  },
  substrateSettings: {
    default: {
      Component: SubstrateSettings,
    },
  },
  support: {
    default: {
      Component: SupportForm,
    },
  },
  [TARGET_RANGES]: {
    view: {
      Component: DayNightTargetRangeView,
      autoFetch: false,
      noEntity: true,
      actions: ['view_room'],
    },
    default: {
      Component: DayNightTargetRangesDialog,
      autoFetch: false,
      actions: ['change_targetrange'],
      noEntity: true,
    },
    deleting: {
      Component: TargetRangeDelete,
    },
  },
  tasks: {
    new: {
      Component: TaskForm,
      aliases: ['/tasks/new/:date'],
      actions: ['view_task'],
    },
    newIPM: {
      hasId: false,
      Component: TaskForm,
      additionalProps: {
        category: 'PEST',
      },
      actions: ['view_task'],
    },
    view: {
      Component: TaskView,
      actions: ['view_task'],
    },
    completing: {
      Component: TaskView,
      additionalProps: {
        completing: true,
      },
      actions: ['view_task'],
    },
    edit: {
      Component: TaskForm,
      actions: ['view_task'],
    },
    deleting: {
      Component: TaskDelete,
      actions: ['view_task'],
    },
    duplicate: {
      Component: TaskForm,
      actions: ['view_task'],
    },
    review: {
      Component: TaskReviewHours,
      actions: ['view_task'],
    },
    taskEditor: {
      Component: TaskManager,
      facilityTypes: ['CEA', 'CORE'],
      actions: ['change_task'],
      pattern: `/${TASK_EDITOR}`,
      aliases: [TASK_EDITOR_ROUTE],
      params: {},
    },
  },
}

export default Object.entries(SCHEMA_ACTION_TO_ROUTE).reduce((acc, [schema, actions]) => {
  Object.entries(actions).forEach(([action, opts]) => {
    const isDefault = action === 'default'
    const isNew = action === 'new'
    const isView = action === 'view'
    const {
      aliases,
      hasId = !isDefault && !isNew,
      hasAction = !isDefault && !isView,
      pattern: optsPattern,
      ...restOpts
    } = opts
    const params = { schema, action }
    let pattern = optsPattern
    if (!pattern) {
      if (hasId) {
        pattern = hasAction ? `/${schema}/:id/${action}` : `/${schema}/:id`
      } else {
        pattern = hasAction ? `/${schema}/${action}` : `/${schema}`
      }
    }

    const config = {
      params,
      ...restOpts,
    }

    if (config.actions && !config.PermissionFallback) {
      config.PermissionFallback = Forbidden
    }
    if (config.features && !config.Fallback) {
      config.Fallback = NotFound
    }

    acc[pattern] = config

    if (Array.isArray(aliases)) {
      aliases.forEach(alias => {
        acc[alias] = config
      })
    }
  })
  return acc
}, {})
